import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, PlayCircle, Check, Sparkles } from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/marketing/MarketingChrome";
import { MARKETING } from "@/lib/marketingContent";

const TOUR = MARKETING.tour;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Frame = ({ src, alt }) => (
  <figure className="overflow-hidden rounded-md border border-[color:var(--marketing-border)] bg-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)]">
    <div className="flex items-center gap-1.5 border-b border-[color:var(--marketing-border)] bg-slate-50 px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
      <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
      <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
      <span className="ml-3 font-marketing-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">
        {alt}
      </span>
    </div>
    <img src={src} alt={alt} loading="lazy" className="block w-full" />
  </figure>
);

const VideoPanel = () => {
  const [playing, setPlaying] = React.useState(false);
  const url = TOUR.videoUrl;

  if (!url) {
    return (
      <div data-testid="tour-video-placeholder"
        className="relative overflow-hidden rounded-md border border-[color:var(--marketing-border)]">
        <img src={TOUR.videoPoster} alt="" aria-hidden className="block w-full opacity-40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center"
          style={{ backgroundColor: "rgba(11,18,32,0.9)" }}>
          <PlayCircle size={40} strokeWidth={1.1} className="text-white/60" />
          <p className="font-marketing-display text-lg font-bold text-white md:text-xl">
            {TOUR.videoPending.heading}
          </p>
          <p className="max-w-lg text-sm leading-relaxed text-white/60">{TOUR.videoPending.body}</p>
          <Link to="/contact" data-testid="tour-book-walkthrough"
            className="mt-1 inline-flex items-center gap-1.5 rounded-sm bg-white px-4 py-2.5 text-sm font-semibold text-[color:var(--marketing-text)] transition-transform hover:-translate-y-0.5">
            Arrange a live walkthrough <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    );
  }

  const isEmbed = /youtube|vimeo|synthesia|embed/.test(url);
  if (isEmbed) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-md border border-[color:var(--marketing-border)] bg-black">
        <iframe data-testid="tour-video-embed" src={url} title="Videra PM product tour"
          className="h-full w-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-md border border-[color:var(--marketing-border)] bg-black">
      <video data-testid="tour-video" src={url} controls={playing} autoPlay={playing}
        poster={TOUR.videoPoster} className="h-full w-full object-cover" />
      {!playing && (
        <button data-testid="tour-video-play" onClick={() => setPlaying(true)}
          className="absolute inset-0 flex items-center justify-center bg-black/35 transition-colors hover:bg-black/20">
          <PlayCircle size={64} strokeWidth={1} className="text-white" />
        </button>
      )}
    </div>
  );
};

const Chapter = ({ chapter, index }) => {
  const Icon = Icons[chapter.icon] || Icons.Square;
  const flip = index % 2 === 1;
  return (
    <section
      id={slug(chapter.title)}
      data-testid={`tour-chapter-${index}`}
      className={`scroll-mt-36 border-t border-[color:var(--marketing-border)] py-16 lg:py-20 ${flip ? "bg-white" : ""}`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
            <p className="inline-flex items-center gap-2 font-marketing-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
              <Icon size={14} strokeWidth={1.5} className="text-[color:var(--marketing-primary)]" />
              {chapter.minutes}
            </p>
            <h3 className="mt-4 font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl">
              {chapter.title}
            </h3>
            <p className="mt-5 text-base font-semibold leading-relaxed text-[color:var(--marketing-text)]">
              {chapter.lead}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-[color:var(--marketing-text-muted)]">
              {chapter.body}
            </p>
            <ul className="mt-7 space-y-3">
              {chapter.callouts.map((c) => (
                <li key={c} className="flex gap-2.5 text-sm text-[color:var(--marketing-text)]">
                  <Check size={15} strokeWidth={2} className="mt-0.5 shrink-0 text-[color:var(--marketing-primary)]" />
                  {c}
                </li>
              ))}
            </ul>
            <p className="mt-7 border-l-2 border-[color:var(--marketing-primary)] pl-4 font-marketing-display text-base font-semibold tracking-tight">
              {chapter.takeaway}
            </p>
          </div>
          <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
            <Frame src={chapter.image} alt={chapter.title} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Tour() {
  return (
    <div className="marketing-root min-h-screen bg-[color:var(--marketing-bg)] text-[color:var(--marketing-text)]">
      <MarketingNav active="Tour" />
      <main data-testid="tour-page">
        {/* Hero */}
        <section className="mx-auto max-w-7xl px-6 pb-14 pt-16 lg:px-10 lg:pt-20">
          <p className="overline-label">{TOUR.overline}</p>
          <h1 className="mt-4 max-w-3xl font-marketing-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {TOUR.heading}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--marketing-text-muted)] md:text-lg">
            {TOUR.sub}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/signup" data-testid="tour-start-trial"
              className="inline-flex items-center gap-1.5 rounded-sm bg-[color:var(--marketing-primary)] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--marketing-primary-hover)]">
              Start a free trial <ArrowRight size={15} strokeWidth={1.5} />
            </Link>
            <Link to="/pricing" data-testid="tour-buy-now"
              className="inline-flex items-center gap-1.5 rounded-sm border-2 border-slate-200 px-5 py-3 text-sm font-semibold transition-colors hover:border-slate-900">
              Buy now
            </Link>
            <Link to="/contact" data-testid="tour-talk-to-us"
              className="text-sm font-semibold text-[color:var(--marketing-primary)] hover:underline">
              Or speak to us first
            </Link>
          </div>
        </section>

        {/* Video */}
        <section className="mx-auto max-w-6xl px-6 lg:px-10">
          <VideoPanel />
        </section>

        {/* Workspace facts */}
        <section className="mt-16 border-y border-[color:var(--marketing-border)] bg-white py-10">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <p className="overline-label">{TOUR.workspace.label}</p>
            <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="tour-workspace-facts">
              {TOUR.workspace.items.map(([head, sub]) => (
                <div key={head} className="border-t-2 border-slate-900 pt-3">
                  <p className="font-marketing-display text-lg font-bold tracking-tight">{head}</p>
                  <p className="mt-1 text-sm text-[color:var(--marketing-text-muted)]">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Chapter rail */}
        <div className="sticky top-[65px] z-30 border-b border-[color:var(--marketing-border)] bg-[color:var(--marketing-bg)]/95 backdrop-blur-md">
          <div className="mx-auto max-w-7xl overflow-x-auto px-6 lg:px-10">
            <nav className="flex items-center gap-6 py-3.5" data-testid="tour-chapter-rail">
              {TOUR.chapters.map((c, i) => (
                <a key={c.title} href={`#${slug(c.title)}`}
                  data-testid={`tour-rail-${i}`}
                  className="shrink-0 whitespace-nowrap text-xs font-medium text-[color:var(--marketing-text-muted)] transition-colors hover:text-[color:var(--marketing-primary)]">
                  <span className="font-marketing-mono text-[10px] text-slate-400">{String(i + 1).padStart(2, "0")}</span>{" "}
                  {c.title}
                </a>
              ))}
            </nav>
          </div>
        </div>

        {/* Intro to chapters */}
        <section className="mx-auto max-w-7xl px-6 pt-16 lg:px-10">
          <p className="overline-label">{TOUR.chaptersOverline}</p>
          <h2 className="mt-3 max-w-2xl font-marketing-display text-2xl font-bold tracking-tight md:text-3xl lg:text-4xl">
            {TOUR.chaptersHeading}
          </h2>
        </section>

        <div className="mt-12" data-testid="tour-chapters">
          {TOUR.chapters.map((c, i) => <Chapter key={c.title} chapter={c} index={i} />)}
        </div>

        {/* What it replaces */}
        <section className="border-t border-[color:var(--marketing-border)] bg-white py-16">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <p className="overline-label">{TOUR.replaces.overline}</p>
            <h2 className="mt-3 font-marketing-display text-2xl font-bold tracking-tight md:text-3xl">
              {TOUR.replaces.heading}
            </h2>
            <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="tour-replaces">
              {TOUR.replaces.items.map((r) => (
                <li key={r} className="border-t border-[color:var(--marketing-border)] pt-4 text-sm text-[color:var(--marketing-text-muted)]">
                  <span className="mb-2 block font-marketing-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    Replaced
                  </span>
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="bg-[color:var(--marketing-inverse-bg)] py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <p className="inline-flex items-center gap-2 font-marketing-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
              <Sparkles size={13} strokeWidth={1.5} /> Ready when you are
            </p>
            <h2 className="mt-4 max-w-3xl font-marketing-display text-2xl font-bold tracking-tight text-white md:text-3xl lg:text-4xl">
              {TOUR.cta.heading}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/65">{TOUR.cta.body}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="tour-cta-trial"
                className="inline-flex items-center gap-1.5 rounded-sm bg-white px-5 py-3 text-sm font-semibold text-[color:var(--marketing-text)] transition-transform hover:-translate-y-0.5">
                Start free trial <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
              <Link to="/pricing" data-testid="tour-cta-pricing"
                className="inline-flex items-center gap-1.5 rounded-sm border border-white/25 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-white">
                See pricing
              </Link>
            </div>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
